import { useEffect, useMemo, useState, useRef } from "react";
import scanTicket from "../../../utility/scanTicket";
import BackImage from "../../../images/icons/back.png";
import "./participant.scss";
import PropTypes from "prop-types";
import OutlinedPinkButton from "../../buttons/outlinedPinkButton/OutlinedPinkButton";
import { useTranslation } from "react-i18next";
import ModalComponent from "../../modal/Modal";
import { downloadPDF, sendEmail } from "../../../utility/createDownloadTicket";
import ScanOkImage from "../../../images/scan_ok.png";
import ScanNotOkImage from "../../../images/scan_not_ok.png";
import deleteParticipant from "../../../utility/deleteParticipant";
import ENV from "../../../env";

function Participant({
  participant: initialParticipant,
  loadDetail,
  event,
  backgroundColor,
  fetchParticipants,
  onParticipantCancelled,
}) {
  const [loadedParticipant, setLoadedParticipant] = useState(null);
  const participant = loadedParticipant || initialParticipant;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const [showInformation, setShowInformation] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [isScanned, setIsScanned] = useState(participant.isRead);
  const [emailToCheck, setEmailToCheck] = useState("");
  const [emailError, setEmailError] = useState("");
  const [modalInformations, setModalInformations] = useState({
    content: "",
    title: "",
  });
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const { t } = useTranslation("participant");
  const validationLock = useRef(false);
  const participantTickets = Array.isArray(participant?.tickets) ? participant.tickets : [];
  const scanCount = Number.isFinite(Number(participant?.numberOfScan))
    ? Number(participant.numberOfScan)
    : 0;
  const isBusy = activeAction !== null;
  const isCancelling = activeAction === "cancellation";
  const isSendingEmail = activeAction === "email";
  const detailsPanelId = `participant-details-${participant.id}`;

  const participantDetails = useMemo(() => {
    const details = Array.isArray(participant?.details) ? participant.details : [];
    return details.map((detail, index) => ({
      key: `${detail?.name || "detail"}-${index}`,
      name: String(detail?.name || ""),
      value: Array.isArray(detail?.value)
        ? detail.value.filter(Boolean).join(", ")
        : String(detail?.value ?? ""),
    }));
  }, [participant?.details]);

  const participantIdentity = useMemo(() => {
    const detailMap = new Map(
      participantDetails.map((detail) => [detail.name, detail.value.trim()]),
    );
    const firstName = detailMap.get("Firstname") || "";
    const lastName = detailMap.get("Name") || "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ") || t("unnamedParticipant");
    const initials =
      [firstName, lastName]
        .filter(Boolean)
        .map((value) => value.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase() || "?";

    return {
      email: detailMap.get("Email") || "",
      fullName,
      initials,
    };
  }, [participantDetails, t]);

  useEffect(() => {
    setIsScanned(Boolean(participant.isRead));
  }, [participant.isRead]);

  const formatAmount = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "0.00 €";
    return `${numericValue.toFixed(2)} €`;
  };

  async function handleDownloadPDF() {
    if (isBusy) return;

    setActiveAction("download");
    try {
      await downloadPDF({ participant, event });
    } catch (error) {
      console.error("Error downloading ticket:", error);
      setModalInformations({
        title: t("operationErrorTitle"),
        content: t("downloadError"),
      });
    } finally {
      setActiveAction(null);
    }
  }

  async function handleSendEmail() {
    if (isBusy) return;

    const recipientEmail = emailToCheck.trim();
    if (!recipientEmail) {
      setEmailError(t("emailRequired"));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      setEmailError(t("invalidEmail"));
      return;
    }

    setActiveAction("email");
    try {
      await sendEmail({
        participant,
        event,
        recipientEmail,
        successUrl: `${ENV.hostname}/checkout/${event.id}/${participant.id}/success`,
      });
      setIsEmailModalOpen(false);
      setModalInformations({
        title: t("emailSentSuccessfully"),
        content: `${t("emailSentToAddress")} ${recipientEmail}`,
      });
      setEmailToCheck("");
      setEmailError("");
    } catch (error) {
      setEmailError(t("tryAgainLater"));
      console.error("Error sending email:", error);
    } finally {
      setActiveAction(null);
    }
  }

  async function handleValidateTicket() {
    if (isBusy || validationLock.current) return;

    setActiveAction("validation");
    try {
      validationLock.current = true;
      const result = await scanTicket({ eventId: event.id, participantId: participant.id, action: isScanned ? 'out' : 'in' });
      if (!result.valid && !['already_scanned', 'already_out'].includes(result.reason)) throw new Error(result.reason);
      setIsScanned(result.participant.isRead);
      await fetchParticipants(participant.id);
    } catch (error) {
      console.error("Error validating ticket:", error);
      setModalInformations({
        title: t("operationErrorTitle"),
        content: t(`scanErrors.${error.message}`, { defaultValue: t("validationError") }),
      });
    } finally {
      validationLock.current = false;
      setActiveAction(null);
    }
  }

  async function handleCancelParticipant() {
    if (isBusy) return;

    setActiveAction("cancellation");
    try {
      const result = await deleteParticipant(participant, event.id);
      setIsCancellationModalOpen(false);
      await onParticipantCancelled({
        participantId: participant.id,
        participantName: participantIdentity.fullName,
        inventoryWarnings: result?.inventoryWarnings || [],
      });
    } catch (error) {
      console.error("Error cancelling participant:", error);
      setIsCancellationModalOpen(false);
      setModalInformations({
        title: t("cancellationErrorTitle"),
        content: `${t("cancellationErrorMessage")}${
          error?.message ? `\n${t("technicalDetails")}: ${error.message}` : ""
        }`,
      });
    } finally {
      setActiveAction(null);
    }
  }

  return (
    <article
      className={`participantContainer ${showInformation ? "isExpanded" : ""}`}
      aria-busy={isBusy}
    >
      {isBusy && !isSendingEmail && !isCancelling ? (
        <div className="participantBusyOverlay" role="status">
          <span className="participantBusySpinner" aria-hidden="true" />
          <span>{t("operationInProgress")}</span>
        </div>
      ) : null}
      <ModalComponent
        isOpen={!!modalInformations.title}
        onClose={() => setModalInformations({ title: "", content: "" })}
        title={modalInformations.title}
        message={modalInformations.content}
      />
      <ModalComponent
        isOpen={isEmailModalOpen}
        onClose={() => {
          if (isSendingEmail) return;
          setIsEmailModalOpen(false);
          setEmailToCheck("");
          setEmailError("");
        }}
        title={t("enterParticipantEmail")}
        shouldNotCloseOnOverlayClick={isSendingEmail}
        hideCloseButton={isSendingEmail}
        actionsComponent={
          <div className="emailVerificationForm">
            <div className="participantEmailField">
              <input
                id={`participant-email-${participant.id}`}
                type="email"
                value={emailToCheck}
                onChange={(event) => {
                  setEmailToCheck(event.target.value);
                  if (emailError) setEmailError("");
                }}
                placeholder={t("emailPlaceholder")}
                aria-label={t("emailPlaceholder")}
                disabled={isSendingEmail}
                aria-invalid={Boolean(emailError)}
                aria-describedby={
                  emailError ? `participant-email-error-${participant.id}` : undefined
                }
              />
              {emailError ? (
                <p id={`participant-email-error-${participant.id}`} className="emailErrorMessage">
                  {emailError}
                </p>
              ) : null}
            </div>
            <OutlinedPinkButton onPressFunction={handleSendEmail} disabled={isSendingEmail}>
              {isSendingEmail ? t("sendingTicket") : t("send")}
            </OutlinedPinkButton>
            {isSendingEmail ? (
              <div className="participantEmailSendingStatus" role="status">
                <span className="participantBusySpinner" aria-hidden="true" />
                <span>{t("sendingTicket")}</span>
              </div>
            ) : null}
          </div>
        }
      />
      <ModalComponent
        isOpen={isCancellationModalOpen}
        onClose={() => {
          if (!isCancelling) setIsCancellationModalOpen(false);
        }}
        title={t("cancelParticipantTitle")}
        message={t("cancelParticipantWarning")}
        hideCloseButton
        shouldNotCloseOnOverlayClick={isCancelling}
        modalClassName="participantCancellationModal"
        actionsComponent={
          <div className="participantCancellationActions">
            <button
              type="button"
              className="participantCancellationBack"
              onClick={() => setIsCancellationModalOpen(false)}
              disabled={isCancelling}
            >
              {t("keepParticipant")}
            </button>
            <button
              type="button"
              className="participantCancellationConfirm"
              onClick={handleCancelParticipant}
              disabled={isCancelling}
            >
              {isCancelling ? t("cancellingParticipant") : t("confirmCancellation")}
            </button>
          </div>
        }
      />
      <button
        type="button"
        className="participantHeader"
        onClick={async () => {
          if (isBusy) return;
          if (showInformation) {
            setShowInformation(false);
            return;
          }
          if (initialParticipant.lightweight && loadDetail) {
            setActiveAction("details");
            try {
              const result = await loadDetail(initialParticipant.id);
              if (!mounted.current) return;
              setLoadedParticipant(result);
              setShowInformation(true);
            } catch {
              if (mounted.current) setModalInformations({
                title: t("operationErrorTitle"), content: t("tryAgainLater"),
              });
            } finally {
              if (mounted.current) setActiveAction(null);
            }
          } else setShowInformation(true);
        }}
        aria-expanded={showInformation}
        aria-controls={detailsPanelId}
      >
        <span className="participantName">
          <span
            className="avatar"
            style={{
              backgroundColor: backgroundColor,
            }}
            aria-hidden="true"
          >
            {participantIdentity.initials}
          </span>
          <span className="participantIdentity">
            <span className="participantFullName">{participantIdentity.fullName}</span>
            {participantIdentity.email ? (
              <span className="participantEmail">{participantIdentity.email}</span>
            ) : null}
            <span className="participantId">
              {t("participantId")}: {participant.id}
            </span>
          </span>
        </span>
        <span className={`participantStatus ${isScanned ? "isScanned" : "isPending"}`}>
          <img
            className="scanIcon"
            src={isScanned ? ScanOkImage : ScanNotOkImage}
            alt=""
            aria-hidden="true"
          />
          <span>{isScanned ? t("scanned") : t("notScanned")}</span>
        </span>
        <span className="participantScanCount" title={t("numberOfScans")}>
          <span className="participantScanCountValue">{scanCount}</span>
          <span className="participantScanCountLabel">{t("scansShort")}</span>
        </span>
        <img
          className="arrow"
          src={BackImage}
          alt=""
          aria-hidden="true"
          style={{ transform: showInformation ? "rotate(90deg)" : "rotate(-90deg)" }}
        />
        <span className="participantSrOnly">
          {showInformation ? t("hideDetails") : t("showDetails")}
        </span>
      </button>

      {showInformation && (
        <div className="participantInformations" id={detailsPanelId}>
          <dl className="participantDetailsList">
            {participantDetails.map((detail) => (
              <div className="participantInformation" key={detail.key}>
                <dt className="participantInformationTitle">{detail.name}</dt>
                <dd className="participantInformationValue">{detail.value || "-"}</dd>
              </div>
            ))}
            {participant?.scannedBy && (
              <div className="participantInformation">
                <dt className="participantInformationTitle">{t("scannedBy")}</dt>
                <dd className="participantInformationValue">{participant.scannedBy}</dd>
              </div>
            )}

            <div className="participantInformation">
              <dt className="participantInformationTitle">{t("numberOfScans")}</dt>
              <dd className="participantInformationValue">{scanCount}</dd>
            </div>
          </dl>

          <div className="participantPurchasedTickets">
            <h3 className="participantPurchasedTicketsTitle">{t("purchasedTickets")}</h3>
            {participantTickets.length === 0 ? (
              <p className="participantPurchasedTicketsEmpty">{t("noPurchasedTickets")}</p>
            ) : (
              <div className="participantPurchasedTicketsList">
                {participantTickets.map((ticket, index) => (
                  <div
                    className="participantPurchasedTicketItem"
                    key={`${ticket.ticketId || ticket.name || "ticket"}-${index}`}
                  >
                    <p className="participantPurchasedTicketName">{ticket.name || "-"}</p>
                    <span className="participantPurchasedTicketQuantity">
                      {ticket.number ?? 0} ×
                    </span>
                    <span className="participantPurchasedTicketTotal">
                      {formatAmount(ticket.sum)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ticketsButtons">
            <div className="ticketPrimaryActions">
              <OutlinedPinkButton onPressFunction={handleDownloadPDF} disabled={isBusy}>
                {activeAction === "download" ? t("downloadingTicket") : t("downloadTicket")}
              </OutlinedPinkButton>
              <OutlinedPinkButton
                onPressFunction={() => {
                  setEmailToCheck(participantIdentity.email);
                  setEmailError("");
                  setIsEmailModalOpen(true);
                }}
                disabled={isBusy}
              >
                {t("sendTicketMail")}
              </OutlinedPinkButton>
            </div>
            {!participantIdentity.email ? (
              <p className="participantActionHint">{t("missingParticipantEmail")}</p>
            ) : null}
            <div className="ticketValidationAction">
              {isScanned ? (
                <button
                  type="button"
                  className="ticketValidationButton resetValidationButton"
                  onClick={() => {
                    if (window.confirm(t("confirmDeValidate"))) handleValidateTicket();
                  }}
                  disabled={isBusy}
                >
                  {activeAction === "validation" ? t("updatingTicket") : t("deValidateTicket")}
                </button>
              ) : (
                <button
                  type="button"
                  className="ticketValidationButton validateTicketButton"
                  onClick={handleValidateTicket}
                  disabled={isBusy}
                >
                  {activeAction === "validation" ? t("updatingTicket") : t("validateTicket")}
                </button>
              )}
            </div>
            <button
              type="button"
              className="cancelParticipantButton"
              onClick={() => setIsCancellationModalOpen(true)}
              disabled={isBusy}
            >
              {t("cancelParticipant")}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

Participant.propTypes = {
  loadDetail: PropTypes.func,
  participant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isRead: PropTypes.bool,
    scannedBy: PropTypes.string,
    numberOfScan: PropTypes.number,
    tickets: PropTypes.arrayOf(PropTypes.object),
    details: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
          .isRequired,
      }),
    ).isRequired,
  }),
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  backgroundColor: PropTypes.string.isRequired,
  fetchParticipants: PropTypes.func.isRequired,
  onParticipantCancelled: PropTypes.func.isRequired,
};

export default Participant;
